
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BlogPost } from "@/types/Blog";
import { CATEGORY_ICONS } from "@/utils/image/constants";
import { useBlogImage } from "@/hooks/useBlogImage";
import { Clock, Briefcase, DollarSign, Megaphone, Code, Palette, FileText, ArrowRight } from "lucide-react";

interface BlogPostCardProps {
  post: BlogPost;
}

export function BlogPostCard({ post }: BlogPostCardProps) {
  const { imageUrl, loading, error } = useBlogImage(post.slug, post.category);
  const categoryIcon = CATEGORY_ICONS[post.category] || CATEGORY_ICONS['default'];
  
  return (
    <Card className="overflow-hidden hover:shadow-md transition-all duration-300 border-border/40 h-full flex flex-col group animate-fade-in">
      <Link to={`/blog/${post.slug}`} className="flex-1 flex flex-col">
        <div className="aspect-[16/9] overflow-hidden relative bg-muted/50">
          {loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-primary opacity-75">
                {categoryIcon === 'clock' && <Clock size={48} />}
                {categoryIcon === 'briefcase' && <Briefcase size={48} />}
                {categoryIcon === 'dollar-sign' && <DollarSign size={48} />}
                {categoryIcon === 'megaphone' && <Megaphone size={48} />}
                {categoryIcon === 'code' && <Code size={48} />}
                {categoryIcon === 'palette' && <Palette size={48} />}
                {categoryIcon === 'file-text' && <FileText size={48} />}
              </div>
            </div>
          ) : (
            <>
              <img 
                src={imageUrl} 
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </>
          )}
        </div>
        
        <CardContent className="pt-4 flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-md">{post.category}</span>
            <span className="text-muted-foreground">•</span>
            <span>{new Date(post.publishedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}</span>
          </div>
          
          <h2 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
            {post.title}
          </h2>
          
          <p className="text-muted-foreground text-sm line-clamp-2">{post.excerpt}</p>
          
          <div className="mt-4 text-primary flex items-center text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Read more <ArrowRight size={14} className="ml-1" />
          </div>
        </CardContent>
      </Link>
      
      <CardFooter className="border-t border-border/20 px-4 py-3 bg-muted/10">
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6 border">
            <AvatarImage src={post.author.avatar || '/placeholder.svg'} alt={post.author.name} />
            <AvatarFallback>{post.author.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs font-medium">{post.author.name}</p>
            <p className="text-xs text-muted-foreground">{post.readingTime} min read</p>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
